import React from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, FileText, CheckCircle, Sparkle } from "@phosphor-icons/react";

/**
 * WOOCRPanel — extracted from ProjectEditSheet.jsx (iter49 refactor).
 *
 * Renders the "Signed WO file" upload row + AI OCR "Auto-Import from WO"
 * button + the "N BOQ items extracted" callout that offers to create a BOQ
 * automatically from the extracted items.
 *
 * All handlers are passed in from the parent so we don't have to move any
 * of the form-state ownership.
 *
 * Props
 *   • woFile / setWoFile               — pending file to upload
 *   • ocrBusy                          — spinner flag
 *   • existingAttachmentId             — non-empty when a WO file is already on the project
 *   • onAutoImport()                   — kicks off AI OCR extraction
 *   • extractedItems[] | null          — the AI-extracted BOQ item list
 *   • extractedSplits | null           — payment splits from the WO
 *   • onCreateBoq()                    — creates a BOQ from the extracted items
 *   • onDismissExtracted()             — clear the extracted-items callout
 */
export default function WOOCRPanel({ woFile, setWoFile, ocrBusy, existingAttachmentId, onAutoImport, extractedItems, extractedSplits, onCreateBoq, onDismissExtracted }) {
  return (
    <div className="border border-slate-200 rounded-sm p-3 bg-slate-50">
      <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-2 block">Signed WO file (PDF / image)</Label>
      <div className="flex items-center gap-2 flex-wrap">
        <Input
          type="file"
          accept="application/pdf,image/*"
          onChange={(e) => setWoFile(e.target.files?.[0] || null)}
          data-testid="wo-file-input"
          className="flex-1 min-w-[240px]"
        />
        <Button
          size="sm"
          onClick={() => {
            if (!woFile) return toast.error("Pick a WO file first (PDF or image)");
            onAutoImport();
          }}
          disabled={!woFile || ocrBusy}
          className="h-9 rounded-sm bg-gradient-to-r from-[#EA580C] to-[#c2410c] hover:from-[#c2410c] hover:to-[#9a3412] text-white font-semibold"
          data-testid="wo-auto-import"
          title="Extract WO fields from this PDF/image using AI OCR"
        >
          <Sparkle size={13} className="mr-1.5" /> {ocrBusy ? "Extracting…" : "Auto-Import from WO"}
        </Button>
        {existingAttachmentId && !woFile && (
          <span className="text-[11px] text-emerald-700 flex items-center gap-1 whitespace-nowrap">
            <CheckCircle size={13} /> File on record
          </span>
        )}
        {woFile && !ocrBusy && (
          <span className="text-[11px] text-[#EA580C] font-semibold flex items-center gap-1 whitespace-nowrap">
            <Upload size={13} /> Uploads on save
          </span>
        )}
      </div>
      {existingAttachmentId && (
        <div className="text-[10.5px] text-slate-500 mt-1 font-mono">ID: {existingAttachmentId}</div>
      )}
      <div className="text-[10.5px] text-slate-500 mt-1.5">
        Tip: click <b>Auto-Import from WO</b> to have AI extract WO#, date, value,
        agreement, GSTINs and payment terms — the file will still upload on save.
      </div>

      {extractedItems && extractedItems.length > 0 && (
        <div className="mt-3 p-3 bg-[#0F172A] text-white rounded-sm" data-testid="wo-boq-callout">
          <div className="flex items-center justify-between mb-2">
            <div className="font-display font-bold text-[14px] flex items-center gap-2">
              <Sparkle size={14} className="text-[#EA580C]" /> {extractedItems.length} BOQ item(s) extracted from the WO
            </div>
            <button className="text-[11px] text-white/70 hover:text-white" onClick={onDismissExtracted}>Dismiss ✕</button>
          </div>
          <div className="text-[11.5px] text-white/80 mb-2">
            Payment splits detected: Supply {extractedSplits?.supply_pct || 85}% · Install {extractedSplits?.install_pct || 10}% · T&C {extractedSplits?.tnc_pct || 5}%.
          </div>
          <div className="max-h-24 overflow-y-auto text-[10.5px] font-mono opacity-80 mb-2">
            {extractedItems.slice(0, 5).map((it, i) => (
              <div key={`ext-${it.description || it.sr_no || i}`}>
                • {it.description?.slice(0, 60)} · qty={it.contract_qty} · rate=₹{it.rate}
              </div>
            ))}
            {extractedItems.length > 5 && <div>... + {extractedItems.length - 5} more</div>}
          </div>
          <Button
            size="sm"
            onClick={onCreateBoq}
            className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white font-semibold"
            data-testid="wo-create-boq"
          >
            <FileText size={13} className="mr-1.5" /> Create BOQ automatically
          </Button>
        </div>
      )}
    </div>
  );
}
